import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Rating,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import apiService from "../../../service/Api";

interface Props {
  streamId: number | string;
}

interface StreamFeedbackItem {
  id: number;
  rating: number;
  comment: string | null;
  created_at: string;
}

interface StreamFeedbackResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: StreamFeedbackItem[];
}

const PREVIEW_COUNT = 2;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function FeedbackRow({ item }: { item: StreamFeedbackItem }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Rating value={item.rating} readOnly size="small" />
        <Typography variant="caption" color="text.secondary">
          {formatDate(item.created_at)}
        </Typography>
      </Stack>
      {item.comment && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          &ldquo;{item.comment}&rdquo;
        </Typography>
      )}
    </Box>
  );
}

const StreamFeedbackSummaryCard: React.FC<Props> = ({ streamId }) => {
  const [items, setItems] = useState<StreamFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.get<StreamFeedbackResponse>(
        `/api/streams/stream-rating/?stream=${streamId}`,
      );
      setItems(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch stream feedback", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  useEffect(() => {
    if (!streamId) return;
    fetchFeedback();
  }, [streamId, fetchFeedback]);

  if (loading) {
    return (
      <Card sx={{ boxShadow: 0 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!items.length) {
    return (
      <Card sx={{ boxShadow: 0 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold">
            Viewer Feedback
          </Typography>
          <Typography mt={2} color="text.secondary">
            No feedback submitted yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const average =
    items.reduce((sum, item) => sum + item.rating, 0) / items.length;
  const preview = items.slice(0, PREVIEW_COUNT);

  return (
    <>
      <Card sx={{ boxShadow: 0 }}>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="h6" fontWeight="bold">
              Viewer Feedback
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {items.length} {items.length === 1 ? "response" : "responses"}
            </Typography>
          </Stack>

          <Box mb={1} display="flex" alignItems="center" gap={1}>
            <Rating value={average} precision={0.1} readOnly />
            <Typography variant="body2" color="text.secondary">
              {average.toFixed(1)} / 5
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Stack spacing={1}>
            {preview.map((item) => (
              <FeedbackRow key={item.id} item={item} />
            ))}
          </Stack>

          {items.length > PREVIEW_COUNT && (
            <Button
              fullWidth
              onClick={() => setViewAllOpen(true)}
              sx={{ mt: 2, textTransform: "none" }}
            >
              View all {items.length} responses
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={viewAllOpen}
        onClose={() => setViewAllOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Viewer Feedback
          <IconButton onClick={() => setViewAllOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} divider={<Divider />}>
            {items.map((item) => (
              <FeedbackRow key={item.id} item={item} />
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StreamFeedbackSummaryCard;
